import type { FilterByContent, Operator, Type } from '#types/components/cms/filter-by'
import type { Content } from '#types/content'
import type { RouteLocationNormalizedLoadedGeneric } from 'vue-router'

type VariantsMap = Record<Type, (data: string) => boolean>
type OperatorMapping = Record<Operator, (input: boolean) => boolean>

const operatorMapping: OperatorMapping = {
  'IN': (input: boolean) => input,
  'NOT IN': (input: boolean) => !input
}

const normalizeList = (data: string, separator: RegExp = /[,\n]+/) => data
  .split(separator)
  .map((s) => s.trim().replace(/\n/g, ''))
  .filter(Boolean)

/**
 * Filters an array of CMS components based on the given configuration which is compared against current route's path and query parameters.
 *
 * @remarks
 * This function acts as a dynamic content filtering engine, designed to power CMS-driven
 * pages. It processes a list of filter rules, each associated with a set of components.
 * It evaluates each rule against the current `route` to determine if its components
 * should be included in the final output.
 *
 * The filtering logic is determined by a combination of:
 * - `type`: The context to check (e.g., `'bySku'`, `'byCategory'`, `'byQuery'`).
 * - `filterBy`: The data for the check (e.g., a comma-separated list of SKUs, a query string).
 * - `operator`: The logical condition (`'IN'` or `'NOT IN'`).
 *
 * @param {FilterByContent} [content] - FilterByContent instance, which contains an array of filter configuration objects.
 * Each object defines a condition and the `components` to render if that condition passes
 * and baseline - an array of CMS components, that is rendered if no filters matched.
 * @param {RouteLocationNormalizedLoadedGeneric} route - The active `vue-router` route object, which provides the context (path, query) for evaluation.
 * @returns {Content[]} A new, flattened array containing all `Content` components that satisfy their respective filtering conditions for the current route.
 */
export default (
  content: FilterByContent,
  route: RouteLocationNormalizedLoadedGeneric
): Content[] => {
  const { filters = [], baseline = [] } = content

  const variantsMap: VariantsMap = {
    bySku: (data: string) => normalizeList(data).includes(getUrlId(route.path, 'Product')),
    byCategory: (data: string) => normalizeList(data).includes(getUrlId(route.path, 'Category')),
    byQuery: (data: string) => normalizeList(data, /\n+/).some((split: string) => matchQuery(split, route.query)),
    byPath: (data: string) => normalizeList(data).some((path: string) => route.path.includes(path)),
  }

  const filteredContents = filters
    .flatMap(
      ({ type, filterBy, operator, components }) =>
        operatorMapping[operator]?.(variantsMap[type]?.(filterBy)) ? components : []
    )

  return filteredContents.length ? filteredContents : baseline
}
